import {useCallback, useContext, useEffect, useState} from 'react'
import {Col, Flex, Form, Modal, Row, Typography} from 'antd'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {getRequest, postRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {CircuitContext, SET_SHOW_HIDE_CIRCUIT_POPUP, UPDATE_CHANGE_CIRCUIT_DATA,} from '../context/context'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'

const CircuitFormModal = ({getCircuitListData}) => {
  const intl = useIntl()
  const {vehicle} = useParams()
  const [state, dispatch] = useContext(CircuitContext)
  const [, navDispatch] = useContext(NavContext)
  const [searchFailed, setSearchFailed] = useState(false)
  const [form] = Form.useForm()
  const [signalOptions, setSignalOptions] = useState([])
  const [saving, setSaving] = useState(false)

  const fetchSignalOptions = useCallback(async (search = '') => {
    let res = await getRequest(
      `/api/signal/?vehicle=${vehicle}&search=${search}&limit=10`
    )
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setSearchFailed(true)
      setSignalOptions([])
      return
    }
    setSearchFailed(false)
    setSignalOptions(
      (res?.results || []).map((o) => ({value: o.id, label: o.name}))
    )
  }, [vehicle])

  useEffect(() => {
    fetchSignalOptions()
  }, [fetchSignalOptions])

  const closeFormPopup = () => {
    dispatch({type: SET_SHOW_HIDE_CIRCUIT_POPUP, data: false})
    form.resetFields()
  }

  const saveCircuit = async (values) => {
    setSaving(true)
    const selected = state?.selectedObject || {}
    const formData = new FormData()

    const fields = [
      'name', 'str1', 'str2', 'str3', 'cat1', 'cat2', 'description',
    ]
    fields.forEach((key) => {
      if (values[key] !== undefined && values[key] !== null) {
        formData.append(key, values[key])
      }
    })
    formData.append('vehicle', vehicle)
    formData.append('import_file', selected.import_file || '')
    if (values.signal_name) {
      formData.append(
        'signal_name',
        values.signal_name?.value || values.signal_name
      )
    }

    let res = null
    if (selected?.id) {
      res = await putRequest(
        `/api/circuit/${selected.id}/?vehicle=${vehicle}`,
        formData
      )
    } else {
      res = await postRequest(`/api/circuit/`, formData)
    }

    setSaving(false)

    if (res?.status !== false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({id: 'circuits.saveSuccess'}),
        },
      })
      if (selected?.id) {
        dispatch({
          type: UPDATE_CHANGE_CIRCUIT_DATA,
          data: {...selected, ...values},
        })
      }
      getCircuitListData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({id: 'circuits.saveError'}),
        },
      })
    }
  }

  useEffect(() => {
    if (!state.showCircuitFormPopup) return
    const obj = state?.selectedObject || {}
    form.setFieldsValue({
      name: obj.name || '',
      str1: obj.str1 || '',
      str2: obj.str2 || '',
      str3: obj.str3 || '',
      cat1: obj.cat1 || '',
      cat2: obj.cat2 || '',
      description: obj.description || '',
      signal_name: obj.signal_name
        ? {value: obj.signal_name.id, label: obj.signal_name.name}
        : null,
    })
  }, [state.showCircuitFormPopup])

  return (
    <Modal
      open={state.showCircuitFormPopup}
      title={intl.formatMessage({
        id: state?.selectedObject?.id
          ? 'circuits.editCircuit'
          : 'circuits.newCircuit',
      })}
      width="60%"
      className="circuits-form-modal"
      centered
      footer={null}
      closable
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form form={form} layout="vertical" onFinish={saveCircuit}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <InputText
                name="name"
                label={intl.formatMessage({id: 'circuits.name'})}
                isRequired
              />
            </Col>
            <Col span={12}>
              <InputText
                name="str1"
                label={intl.formatMessage({id: 'circuits.str1'})}
                isRequired={false}
              />
            </Col>
            <Col span={12}>
              <InputText
                name="str2"
                label={intl.formatMessage({id: 'circuits.str2'})}
                isRequired={false}
              />
            </Col>
            <Col span={12}>
              <InputText
                name="str3"
                label={intl.formatMessage({id: 'circuits.str3'})}
                isRequired={false}
              />
            </Col>
            <Col span={12}>
              <InputText
                name="cat1"
                label={intl.formatMessage({id: 'circuits.cat1'})}
                isRequired={false}
              />
            </Col>
            <Col span={12}>
              <InputText
                name="cat2"
                label={intl.formatMessage({id: 'circuits.cat2'})}
                isRequired={false}
              />
            </Col>
            <Col span={12}>
              <InputText
                name="description"
                label={intl.formatMessage({id: 'circuits.description'})}
                isRequired={true}
                type="textarea"
              />
            </Col>
            <Col span={12}>
              <SelectInput
                name="signal_name"
                label={intl.formatMessage({id: 'circuits.signalName'})}
                options={signalOptions}
                notFoundContent={
                  searchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                isRequired={false}
                onInputChange={(value) => {
                  if (value) fetchSignalOptions(value)
                }}
              />
            </Col>
            <Col span={24}>
              <Flex justify="end">
                <Button htmlType="submit" type="primary" loading={saving}>
                  {intl.formatMessage({id: 'circuits.save'})}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default CircuitFormModal
